import React, { FC, useContext, useMemo, useRef } from 'react';
import classNames from 'classnames';
import {
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  ManageEnterprisePeopleTable,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from './data';
import { EnterpriseMember } from 'models/Enterprises';
import { Checkbox } from 'ncoded-component-library';
import { BODY_CONTENT_GENERATE } from './components/ManagePeopleTableCells';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { RightClickDropdown } from 'components/RightClickDropdown';
import EnterpriseMembersContext from 'providers/EnterpriseMembers/EnterpriseMembers.context';
import { ColumnSizing } from 'components/ReorderResizeTable/types';

import './ManagePeopleTable.styles.scss';
import './ManagePeopleTable.styles.responsive.scss';

type ManagePeopleTableProps = {
  className?: string;
  columns: typeof TABLE_COLUMNS;
  columnSizing: ColumnSizing;
  onReorder: (columns: typeof TABLE_COLUMNS) => void;
  onColumnSizingChange: (columnSizing: ColumnSizing) => void;
};

const ManagePeopleTable: FC<ManagePeopleTableProps> = (props) => {
  const { className, columns, columnSizing, onReorder, onColumnSizingChange } =
    props;

  const classes = classNames('manage-people-table', className);

  const {
    members,
    isLoading,
    isAllSelected,
    selected,
    totalPages,
    totalItems,
    onSelectAll,
    updateSelected,
    getDropdownOptions,
    handleEditMember,
    checkIfMemberIsSelected,
  } = useContext(EnterpriseMembersContext);

  const getDropdownOptionsRef = useRef(getDropdownOptions);
  getDropdownOptionsRef.current = getDropdownOptions;

  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const checkIfMemberIsSelectedRef = useRef(checkIfMemberIsSelected);
  checkIfMemberIsSelectedRef.current = checkIfMemberIsSelected;

  const updateSelectedRef = useRef(updateSelected);
  updateSelectedRef.current = updateSelected;

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (member: EnterpriseMember) => (
            <RightClickDropdown
              options={getDropdownOptionsRef.current(
                selectedRef.current?.length ? selectedRef.current : [member],
              )}
            >
              {BODY_CONTENT_GENERATE[col]({
                member,
                isSelectedMember: checkIfMemberIsSelectedRef.current(member),
                onSelectMember: updateSelectedRef.current,
              })}
            </RightClickDropdown>
          ),
        }),
        {},
      ),
    [],
  );

  const headerColTransformation = useMemo(
    () => ({
      checkbox: () => (
        <Checkbox checked={isAllSelected} onChange={onSelectAll} />
      ),
    }),
    [isAllSelected, onSelectAll],
  );

  return (
    <ReorderResizeTable
      className={classes}
      headingTranslations={HEADING_TRANSLATIONS}
      columns={columns}
      sortingValues={SORTING_VALUES}
      isDataLoading={isLoading}
      totalPages={totalPages}
      totalItems={totalItems}
      recordName="member"
      data={members as unknown as ManageEnterprisePeopleTable[]}
      customSizes={CUSTOM_COL_SIZES}
      getIsRowSelected={(member) =>
        checkIfMemberIsSelected(member as unknown as EnterpriseMember)
      }
      onRowClick={(member) =>
        handleEditMember(member as unknown as EnterpriseMember)
      }
      columnSizing={columnSizing}
      onColumnSizingChange={onColumnSizingChange}
      onReorder={onReorder}
      tableHeaderContentTransformations={headerColTransformation}
      tableContentTransformations={renderedContent}
    />
  );
};

export default ManagePeopleTable;
